<template>
  <WdsModal :show="show" lg @cancel="$emit('close')">
    <template #header>
      <h4>{{ $t('Expenses for payment request #:number', { number: requestNumber }) }}</h4>
    </template>

    <WdsStack v-if="loading" justify-center align-center class="tw-py-10">
      <WdsSpinner :size="40" />
    </WdsStack>

    <template v-else-if="paymentRequest">
      <!-- No expenses message -->
      <div v-if="allExpenseReports.length === 0" class="tw-py-5 tw-text-center tw-text-gray-500">
        {{ $t('No expenses found.') }}
      </div>

      <!-- Expense Reports -->
      <WdsStack v-else direction-column gap-md>
        <div
          v-for="report in allExpenseReports"
          :key="report.id"
          class="tw-flex tw-flex-col tw-gap-2 tw-p-4 tw-bg-true-gray-50"
        >
          <!-- Report Header -->
          <div class="tw-flex tw-justify-between tw-w-full">
            <div class="tw-flex tw-gap-sm">
              <div class="tw-mt-0.5">
                <WdsIcon name="report-data" :size="16" />
              </div>
              <WdsText class="tw-flex-1">
                <template #default>
                  <div
                    class="tw-max-w-md tw-overflow-hidden tw-truncate tw-whitespace-nowrap tw-text-ellipsis"
                  >
                    <span class="tw-text-base">{{ report.title || $t('Expense Report') }}</span>
                  </div>
                </template>
                <template v-if="report.description" #subText>
                  <div
                    class="tw-max-w-lg tw-overflow-hidden tw-truncate tw-whitespace-nowrap tw-text-ellipsis"
                  >
                    <span class="tw-text-sm tw-font-medium">{{ report.description }}</span>
                  </div>
                </template>
              </WdsText>
            </div>
            <div class="tw-font-semibold tw-flex tw-gap-md tw-whitespace-nowrap">
              {{ $t('Total') }}:
              <span data-test="expense-report-total">
                {{ formatCurrency(getReportTotal(report), paymentRequest.currency) }}
              </span>
            </div>
          </div>

          <WdsDivider />

          <!-- Expenses List -->
          <div
            class="tw-overflow-x-auto tw-grid tw-grid-cols-[minmax(100px,max-content)_minmax(120px,auto)_minmax(290px,max-content)] tw-items-start tw-gap-x-2 tw-gap-y-3"
          >
            <template v-for="expense in report.expenses?.data || []" :key="expense.id">
              <WdsText>
                <template #default>
                  <div
                    class="tw-max-w-sm tw-overflow-hidden tw-truncate tw-whitespace-nowrap tw-text-ellipsis"
                    :title="expense.vendor"
                  >
                    <span class="tw-text-base">
                      {{ expense.vendor || $t('Expense item name') }}
                    </span>
                  </div>
                </template>
                <template v-if="expense.description" #subText>
                  <div
                    class="tw-max-w-md tw-overflow-hidden tw-truncate tw-whitespace-nowrap tw-text-ellipsis"
                    :title="expense.description"
                  >
                    <span class="tw-text-sm tw-font-medium">{{ expense.description }}</span>
                  </div>
                </template>
              </WdsText>
              <div class="tw-flex tw-flex-col tw-gap-1">
                <span
                  v-for="customFieldValue in expense.customFieldValues"
                  :key="customFieldValue.id"
                  class="tw-truncate tw-text-sm tw-text-gray-500"
                >
                  {{ customFieldValue.customField.title }}: {{ customFieldValue.displayValue }}
                </span>
              </div>
              <div class="tw-grid tw-grid-cols-[auto_minmax(90px,1fr)_minmax(100px,1fr)] tw-items-center">
                <!-- Attachment icon -->
                <div class="tw-flex tw-justify-center tw-w-10">
                  <WdsButtonGhost
                    v-if="hasFiles(expense)"
                    v-tooltip="$t('View attachments')"
                    :href="getFirstFileUrl(expense)"
                    target="_blank"
                    sm
                  >
                    <WdsIcon name="attachment" :size="16" />
                  </WdsButtonGhost>
                  <WdsIcon v-else name="attachment" :size="16" class="tw-text-gray-300" />
                </div>
                <!-- Date -->
                <span class="tw-text-gray-700 tw-whitespace-nowrap tw-flex tw-justify-end">
                  {{ formatDate(expense.expenseAt) }}
                </span>
                <!-- Amount -->
                <div class="tw-flex tw-justify-end">
                  <span class="tw-whitespace-nowrap">
                    {{ formatCurrency(expense.amount, expense.currency) }}
                  </span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </WdsStack>
    </template>

    <template #footer>
      <div class="tw-flex tw-justify-end tw-w-full">
        <WdsButton @click="$emit('close')">{{ $t('Close') }}</WdsButton>
      </div>
    </template>
  </WdsModal>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsModal,
  WdsButton,
  WdsButtonGhost,
  WdsStack,
  WdsSpinner,
  WdsIcon,
  WdsText,
  WdsDivider,
} from '@worksome/wds-components'
import { graphql } from '@/gql'
import useDate from '@/composables/useDate'
import { useTranslation } from '@/composables/translation'
import { toFormattedAmountByLocale } from '@/utils/currency'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'

const props = defineProps<{
  show: boolean
  paymentRequestId: string
}>()

defineEmits<{
  close: []
}>()

const { $t } = useTranslation()

const { result, loading } = useQuery(
  graphql(`
    query PaymentRequestExpenses($id: ID!, $expenseReportsCount: Int!, $expensesCount: Int!) {
      paymentRequest(id: $id) {
        id
        number
        currency
        expenseReports(first: $expenseReportsCount) {
          data {
            id
            title
            description
            total
            expenses(first: $expensesCount) {
              data {
                id
                vendor
                description
                amount
                currency
                expenseAt
                files {
                  data {
                    id
                    name
                    url
                  }
                }
                customFieldValues {
                  id
                  displayValue
                  customField {
                    title
                  }
                }
              }
            }
          }
        }
      }
    }
  `),
  () => ({
    id: props.paymentRequestId,
    expenseReportsCount: PER_PAGE_MAXIMUM,
    expensesCount: PER_PAGE_MAXIMUM,
  }),
  () => ({
    enabled: props.show,
    clientId: 'publicApiCustomFields',
  }),
)

const paymentRequest = computed(() => result.value?.paymentRequest)

// Header info
const requestNumber = computed(() => paymentRequest.value?.number ?? '')

// Expenses data
const allExpenseReports = computed(() => paymentRequest.value?.expenseReports?.data ?? [])

const getReportTotal = (report: (typeof allExpenseReports.value)[0]) => report.total

const hasFiles = (expense: { files?: { data?: unknown[] } }) =>
  (expense.files?.data?.length ?? 0) > 0

const getFirstFileUrl = (expense: { files?: { data?: Array<{ url?: string }> } }) =>
  expense.files?.data?.[0]?.url ?? '#'

// Format currency with code (e.g., "GBP 8,000.00")
const formatCurrency = (amount: number, currency: string): string => {
  const amountInUnits = (amount / 100).toFixed(2)
  const formatted = toFormattedAmountByLocale(amountInUnits, undefined, false)
  return `${currency} ${formatted}`
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-'
  const { date } = useDate(dateString, 'MMM DD, YYYY')
  return date.value ?? '-'
}
</script>
